import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:4000/api";

// Create axios instance with default config
const api = axios.create({
  baseURL: API_URL,
  timeout: 10000, // 10 seconds timeout
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptors for error handling
api.interceptors.response.use(
  (response) => response.data,
  (error) => {
    if (error.response) {
      // Preserve the original data
      error.data = error.response.data; // <-- attach data to error

      // Handle banned users (force logout) - enhanced handling
      if (
        (error.response.status === 403 && error.response.data?.forceLogout) ||
        error.response.data?.banned ||
        error.response.data?.banInfo
      ) {
        console.log("User is banned, forcing logout:", error.response.data);
        
        // Clear stored token
        localStorage.removeItem("token");

        // Show ban message if available
        const banMessage = error.response.data?.message || "Your account has been suspended.";
        
        // Only redirect if we're not already on a login/auth page
        const currentPath = window.location.pathname;
        const isOnAuthPage = currentPath.includes('/auth') || currentPath.includes('/login');
        
        if (!isOnAuthPage) {
          // Dispatch a global event for other parts of the app
          window.dispatchEvent(new CustomEvent('userBanned', { 
            detail: {
              message: banMessage,
              banInfo: error.response.data?.banInfo
            }
          }));

          // Redirect to auth page with login mode
          window.location.href = "/auth?mode=login";
        }
      }

      return Promise.reject(error);
    } else if (error.request) {
      return Promise.reject(new Error("Network error. Please check your connection and try again."));
    } else {
      return Promise.reject(new Error("An unexpected error occurred. Please try again."));
    }
  }
);

/**
 * User API function using Axios
 */
export const userApi = {
  // Login user
  login: async (email, password, remember = false) => {
    return api.post("/user/login", { email, password, remember });
  },

  // Register user
  register: async (userData) => {
    return api.post("/user/register", userData);
  },

  // Get current user profile
  getProfile: async () => {
    return api.get("/user/me");
  },

  // Get user profile by ID
  getUserProfile: async (userId) => {
    return api.get(`/user/${userId}`);
  },

  // Update user profile
  updateProfile: async (profileData) => {
    return api.put("/user/update", profileData);
  },

  // Update user profile image
  updateProfileImage: async (imageFile) => {
    const formData = new FormData();
    formData.append("image", imageFile);

    return api.put("/user/update-image", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  },

  // Delete user account
  deleteAccount: async () => {
    return api.delete("/user/delete");
  },

  updatePassword: async (passwordData) => {
    return api.put("/user/update-password", passwordData)
  },

  // Forgot password
  forgotPassword: async (email) => {
    return api.post("/email/forgot-password", { email });
  },

  // Reset password with token
  resetPassword: async (token, password, confirmPassword) => {
    return api.post(`/email/reset-password/${token}`, { password, confirmPassword });
  },

  // Admin login
  adminLogin: async (email, password) => {
    return api.post("/user/admin", { email, password });
  },

  // Notification Preferences
  updateNotificationPreferences: async (preferences) => {
    return api.put("/user/notification-preferences", {
      emailNotifications: preferences,
    });
  },
};

/**
 * Resources API (public)
 */
export const resourceApi = {
  // Get all resources
  getAllResources: async () => {
    return api.get("/resources");
  },
};

/**
 * Email API functions using Axios
 */
export const emailApi = {
  // Verify email with token
  verifyEmail: async (token) => {
    return api.get(`/email/verify/${token}`);
  },

  // Check token for email (to get email address from expired token)
  checkToken: async (token) => {
    return api.get(`/email/check-token/${token}`);
  },

  // Resend verification email
  resendVerification: async (email) => {
    return api.post("/email/resend-verification", { email });
  },
};

/**
 * Generic API call function (if you need custom requests)
 */
export const apiCall = async (method, endpoint, data = null, config = {}) => {
  try {
    const response = await api({
      method,
      url: endpoint,
      data,
      ...config,
    });
    return response;
  } catch (error) {
    throw error; // Error is already handled by interceptor
  }
};

/**
 * Helper function to cancel requests
 */
export const createCancelToken = () => {
  return axios.CancelToken.source();
};

/**
 * Helper function to check if error is a cancellation
 */
export const isCancel = (error) => {
  return axios.isCancel(error);
};

/**
 * Helper function to check if user is authenticated
 */
export const isAuthenticated = () => {
  const token = localStorage.getItem('token');
  return !!token;
};

/**
 * Helper function to get auth token
 */
export const getAuthToken = () => {
  return localStorage.getItem('token');
};

/**
 * Helper function to clear auth data
 */
export const clearAuthData = () => {
  localStorage.removeItem('token');
};

// Export the axios instance for direct use if needed
export default api;